import { ref } from 'vue'
import { useStore } from 'vuex'
import { useNuxtApp } from 'nuxt/app'
import type { PromoterIntegrationTask } from '@audience-republic/audience-manager-shared/api/promoter-integration-task/types'

// Polling intervals from spec
const ACTIVE_INTERVAL = 5_000 // 5 seconds when tasks are pending/running
const IDLE_INTERVAL = 30_000 // 30 seconds when no tasks are active
const INITIAL_BACKOFF = 5_000 // 5 seconds initial backoff on error
const MAX_BACKOFF = 60_000 // 60 seconds max backoff

interface PollingState {
  timerId: number | null
  isPolling: boolean
  isFetching: boolean
  currentBackoff: number
}

// Singleton state
const state = ref<PollingState>({
  timerId: null,
  isPolling: false,
  isFetching: false,
  currentBackoff: INITIAL_BACKOFF,
})

// Singleton references to store and api (must be initialized from plugin context)
let storeInstance: any = null
let apiInstance: any = null
let isInitialized = false

export function useTaskPolling() {
  // Initialize store and api if not already done
  // Must be called from a proper Nuxt context (plugin, setup, etc.)
  if (!isInitialized) {
    try {
      storeInstance = useStore()
      const nuxtApp = useNuxtApp()
      apiInstance = nuxtApp.$api

      isInitialized = true
    }
    catch (error) {
    }
  }

  /**
   * Determines if any tasks are in PENDING or RUNNING status
   */
  const hasActiveTasks = (tasks: PromoterIntegrationTask[]): boolean => {
    return tasks.some(task =>
      task.status === 'PENDING' || task.status === 'RUNNING',
    )
  }

  /**
   * Fetches tasks and updates store
   */
  const fetchTasks = async (): Promise<void> => {
    if (!storeInstance || !apiInstance) {
      scheduleNextPoll(INITIAL_BACKOFF)
      return
    }

    const promoterOid = storeInstance.state.authModule?.account?.promoterOid

    if (!promoterOid) {
      // Schedule a retry - auth might not be loaded yet
      scheduleNextPoll(INITIAL_BACKOFF) // Retry in 5 seconds
      return
    }

    if (state.value.isFetching) {
      return
    }

    try {
      state.value.isFetching = true
      const api = apiInstance.promoterIntegrationTask

      const [pendingTasks, completedTasks] = await Promise.all([
        api.fetchPending(promoterOid),
        api.fetchCompleted(promoterOid),
      ])

      // Update store
      storeInstance.commit('promoterTasksModule/SET_PENDING_TASKS', pendingTasks)
      storeInstance.commit('promoterTasksModule/SET_COMPLETED_TASKS', completedTasks)

      // Reset backoff on success
      state.value.currentBackoff = INITIAL_BACKOFF

      // Schedule next poll based on task status
      const hasActive = hasActiveTasks(pendingTasks)
      const nextInterval = hasActive ? ACTIVE_INTERVAL : IDLE_INTERVAL
      scheduleNextPoll(nextInterval)
    }
    catch (error) {
      // Apply exponential backoff on error
      const backoffInterval = Math.min(state.value.currentBackoff, MAX_BACKOFF)
      scheduleNextPoll(backoffInterval)

      // Double the backoff for next time (if this error persists)
      state.value.currentBackoff = Math.min(state.value.currentBackoff * 2, MAX_BACKOFF)
    }
    finally {
      state.value.isFetching = false
    }
  }

  /**
   * Schedules the next poll after a given interval
   */
  const scheduleNextPoll = (interval: number): void => {
    // Clear existing timer
    if (state.value.timerId !== null) {
      clearTimeout(state.value.timerId)
      state.value.timerId = null
    }

    // Only schedule if polling is active
    if (state.value.isPolling) {
      state.value.timerId = window.setTimeout(() => {
        fetchTasks()
      }, interval)
    }
  }

  /**
   * Starts polling (called when tab becomes visible)
   */
  const resumePolling = (): void => {
    if (state.value.isPolling) {
      return // Already polling
    }

    state.value.isPolling = true

    // Trigger immediate poll when resuming
    fetchTasks()
  }

  /**
   * Stops polling (called when tab becomes hidden)
   */
  const pausePolling = (): void => {
    state.value.isPolling = false

    if (state.value.timerId !== null) {
      clearTimeout(state.value.timerId)
      state.value.timerId = null
    }
  }

  /**
   * Triggers an immediate poll and resets the timer
   * Used when user clicks or performs an action
   */
  const triggerImmediatePoll = (): void => {
    if (!state.value.isPolling) {
      return // Don't poll if not active
    }

    // Clear existing timer
    if (state.value.timerId !== null) {
      clearTimeout(state.value.timerId)
      state.value.timerId = null
    }

    // Trigger immediate fetch
    fetchTasks()
  }

  /**
   * Manually refresh tasks (exposed for user actions)
   */
  const refreshNow = (): void => {
    triggerImmediatePoll()
  }

  return {
    resumePolling,
    pausePolling,
    triggerImmediatePoll,
    refreshNow,
    isPolling: () => state.value.isPolling,
    isFetching: () => state.value.isFetching,
  }
}
